import ClassNames from "classnames";
import { FunctionComponent, useEffect, useState } from "react";
import { getActiveSite } from "../../selectors/sites.js";
import { injectScript } from "../../slices/loadStates.js";
import { useAppDispatch, useAppSelector } from "../../store/hooks.js";
import styles from "../../stylesheets/components/easipay-module.module.css";
import moduleBaseStyles from "../../stylesheets/components/module-base.module.css";
import {
  BaseModuleProps,
  EasiPayModuleSettings,
  WindowState,
} from "../../types/index.js";
import { getActiveColorScheme } from "../../utils/utils.js";
import LazyloadWrapper from "../LazyloadWrapper.js";
import ModuleHeadings from "../ModuleHeadings.js";
import ModuleWithHeadings from "../ModuleWithHeadings.js";

interface Props extends BaseModuleProps<EasiPayModuleSettings> {}

const EasiPayModule: FunctionComponent<Props> = ({
  translatedModule: {
    id,
    settings: { authId, textAlign },
    translation: {
      languageId,
      settings: { title, subtitle },
    },
  },
  translatedModule,
  isActive,
  isPreview,
  isFirstOnPage,
}) => {
  const dispatch = useAppDispatch();

  const scheme = useAppSelector((state) =>
    getActiveColorScheme(
      state.colorSchemes,
      getActiveSite(state.sites),
      translatedModule,
    ),
  );

  const [isLazyloaded, setIsLazyloaded] = useState(false);

  useEffect(() => {
    authId &&
      isLazyloaded &&
      dispatch(
        injectScript("https://www.easymailing.eu/frontend/easiplugin.php"),
      ).then(() => {
        const windowState = window as unknown as WindowState;
        windowState.loadframe?.();
      });
  }, [isLazyloaded, isPreview, authId]);

  if (!isPreview && !authId) return null;

  return (
    <ModuleWithHeadings
      title={title}
      subtitle={subtitle}
      id={id}
      className={styles.EasiPayModule}
      colors={{ background: scheme.main.background, color: scheme.main.text }}
    >
      <div className={moduleBaseStyles.Module__Wrapper}>
        <ModuleHeadings
          scheme={scheme}
          isFirstOnPage={isFirstOnPage}
          textAlign={textAlign}
          title={title}
          subtitle={subtitle}
        />
      </div>

      <LazyloadWrapper onLoad={setIsLazyloaded}>
        <div
          className={ClassNames(moduleBaseStyles.Module__Wrapper, {
            [styles["EasiPayModule__Content--empty"]]: !authId,
            [styles["EasiPayModule__Content--no-click"]]:
              !isActive && isPreview,
          })}
        >
          <div
            key={authId}
            id="easiplugin_container"
            data-module="easipay"
            data-lang={languageId}
            data-key={authId}
          />
        </div>
      </LazyloadWrapper>
    </ModuleWithHeadings>
  );
};

export default EasiPayModule;
